import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Search, X, ArrowLeft } from "lucide-react";
import Swal from "sweetalert2";

import ImportarAlunos from "../../components/ImportarAlunos/ImportarAlunos";
import { useUsuariosInstituicao } from "../../hooks/useUsuariosInstituicao";
import { usePermissaoAlunos } from "../../hooks/usePermissaoEventos";
import { idsIguais } from "../../utils/ids";
import {
  obterTurma,
  listarAlunos,
  vincularAluno,
  desvincularAluno,
} from "../../api/turmas";
import "./ListaAlunos.scss";

const MSG_SEM_PERMISSAO = "Este usuário não pode efetuar essa ação.";

export default function ListaAlunos() {
  const navigate = useNavigate();
  const { turmaId: routeTurmaId } = useParams();
  // IDs de turma são GUID string — NÃO converter para Number (NaN → "turma não encontrada").
  const turmaId = routeTurmaId || null;

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [instituicaoId, setInstituicaoId] = useState(null);
  const [alunos, setAlunos] = useState([]); // [{ id, nome, matricula }]

  const [buscaAluno, setBuscaAluno] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selecionadosIds, setSelecionadosIds] = useState([]); // buffer pré-adição
  const searchWrapRef = useRef(null);

  const { usuarios: baseUsuarios } = useUsuariosInstituicao(instituicaoId);

  // Permissão "editar lista de alunos" — por turma (a da rota). Coordenador
  // sempre pode. Sem permissão: visualiza, mas não edita.
  const { pode: podeEditarAlunos } = usePermissaoAlunos(turmaId);
  const bloqueado = !podeEditarAlunos;

  const avisarBloqueio = () => {
    Swal.fire({
      title: "Ação não permitida",
      text: MSG_SEM_PERMISSAO,
      icon: "info",
      confirmButtonText: "Entendi",
      confirmButtonColor: "#2E4A67",
    });
  };

  const carregarAlunos = useCallback(async () => {
    if (!turmaId) return;
    const als = await listarAlunos(turmaId);
    setAlunos(
      (Array.isArray(als) ? als : []).map((a) => ({
        id: a.userId,
        nome: a.nome,
        matricula: a.matricula ?? "",
      })),
    );
  }, [turmaId]);

  const carregar = useCallback(async () => {
    if (!turmaId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErro(null);
    try {
      const t = await obterTurma(turmaId);
      setInstituicaoId(t.instituicaoId || null);
      await carregarAlunos();
    } catch (e) {
      setErro(e);
    } finally {
      setLoading(false);
    }
  }, [turmaId, carregarAlunos]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Atualização automática: recarrega a lista quando algo muda (foco da janela
  // dispara turmas:changed globalmente) — sem precisar dar F5.
  useEffect(() => {
    const onChange = () => carregarAlunos();
    window.addEventListener("turmas:changed", onChange);
    window.addEventListener("app:data-changed", onChange);
    return () => {
      window.removeEventListener("turmas:changed", onChange);
      window.removeEventListener("app:data-changed", onChange);
    };
  }, [carregarAlunos]);

  useEffect(() => {
    const onDown = (e) => {
      if (!searchWrapRef.current) return;
      if (!searchWrapRef.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  // Completa matrícula a partir da base de usuários quando o backend não envia.
  const matriculaPorId = useMemo(() => {
    const m = new Map();
    baseUsuarios.forEach((u) => m.set(String(u.id), u.user || ""));
    return m;
  }, [baseUsuarios]);

  const candidatosBusca = useMemo(() => {
    const q = buscaAluno.trim().toLowerCase();
    if (!q) return [];
    const jaVinculados = new Set(alunos.map((a) => String(a.id)));
    const jaNoBuffer = new Set(selecionadosIds.map(String));
    return baseUsuarios
      .filter((u) => String(u.tipo || "").toLowerCase() === "aluno")
      .filter((u) => !jaVinculados.has(String(u.id)))
      .filter((u) => !jaNoBuffer.has(String(u.id)))
      .filter(
        (u) =>
          String(u.nome || "")
            .toLowerCase()
            .includes(q) ||
          String(u.user || "")
            .toLowerCase()
            .includes(q) ||
          String(u.email || "")
            .toLowerCase()
            .includes(q),
      )
      .slice(0, 8)
      .map((u) => ({ id: u.id, nome: u.nome, matricula: u.user }));
  }, [buscaAluno, baseUsuarios, alunos, selecionadosIds]);

  const adicionarAoBuffer = (id) => {
    setSelecionadosIds((prev) =>
      prev.some((x) => idsIguais(x, id)) ? prev : [...prev, id],
    );
    setBuscaAluno("");
    setDropdownOpen(false);
  };

  const removerDoBuffer = (id) =>
    setSelecionadosIds((prev) => prev.filter((x) => !idsIguais(x, id)));

  const handleAdicionarSelecionados = async () => {
    if (!selecionadosIds.length) {
      Swal.fire({ icon: "warning", title: "Selecione um ou mais alunos" });
      return;
    }
    try {
      for (const id of selecionadosIds) {
        if (alunos.some((a) => idsIguais(a.id, id))) continue;
        await vincularAluno(turmaId, id);
      }
      await carregarAlunos();
      setSelecionadosIds([]);
      setBuscaAluno("");
      setDropdownOpen(false);
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Erro ao adicionar",
        text: e?.message,
      });
    }
  };

  const handleRemoverAluno = async (uid) => {
    try {
      await desvincularAluno(turmaId, uid);
      await carregarAlunos();
    } catch (e) {
      Swal.fire({ icon: "error", title: "Erro ao remover", text: e?.message });
    }
  };

  if (!turmaId) {
    return (
      <div className="lista-alunos-page">
        <div className="lista-alunos-card">
          <button
            className="la__back"
            onClick={() => navigate(-1)}
            type="button"
          >
            <ArrowLeft size={14} />
            Voltar
          </button>
          <div className="la__title">Lista de Alunos</div>
          <div className="la__empty">Nenhuma turma selecionada.</div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="lista-alunos-page">
        <div className="lista-alunos-card">
          <div className="la__title">Lista de Alunos</div>
          <div className="la__empty">Carregando…</div>
        </div>
      </div>
    );
  }

  if (erro) {
    return (
      <div className="lista-alunos-page">
        <div className="lista-alunos-card">
          <button
            className="la__back"
            onClick={() => navigate(-1)}
            type="button"
          >
            <ArrowLeft size={14} />
            Voltar
          </button>
          <div className="la__title">Lista de Alunos</div>
          <div className="la__empty">Turma não encontrada.</div>
        </div>
      </div>
    );
  }

  const temSelecionados = selecionadosIds.length > 0;

  return (
    <div className="lista-alunos-page">
      <div className="lista-alunos-card">
        <div className="la__top">
          <button
            className="la__back"
            onClick={() => navigate(-1)}
            type="button"
          >
            <ArrowLeft size={14} />
            Voltar
          </button>
        </div>

        {/* Conteúdo de edição: bloqueado por permissão. "Voltar" fica livre. */}
        <div
          className={`la__conteudo ${bloqueado ? "la__conteudo--bloqueado" : ""}`}
        >
          {bloqueado && (
            <div
              className="permissao-overlay"
              onClick={avisarBloqueio}
              role="button"
              tabIndex={0}
              aria-label={MSG_SEM_PERMISSAO}
            />
          )}

          <div className="la__topActions">
            <ImportarAlunos
              usuarios={baseUsuarios}
              faculdadeId={instituicaoId}
              idsJaExistentes={alunos.map((a) => a.id)}
              buttonLabel="Importar lista"
              className="la__import"
              onImportConfirm={async (idsNovos) => {
                try {
                  for (const id of idsNovos) {
                    if (alunos.some((a) => idsIguais(a.id, id))) continue;
                    await vincularAluno(turmaId, id);
                  }
                  await carregarAlunos();
                } catch (e) {
                  Swal.fire({
                    icon: "error",
                    title: "Erro ao importar",
                    text: e?.message,
                  });
                }
              }}
            />
          </div>

          <div className="la__title">Lista de Alunos</div>
          <div className="la__sub">Adicione alunos</div>

          <div className="la__searchWrap" ref={searchWrapRef}>
            <Search size={16} className="la__searchIco" />
            <input
              value={buscaAluno}
              onChange={(e) => {
                setBuscaAluno(e.target.value);
                setDropdownOpen(true);
              }}
              onFocus={() => {
                if (buscaAluno.trim()) setDropdownOpen(true);
              }}
              placeholder="Busque por nome, matrícula..."
            />

            {dropdownOpen && candidatosBusca.length > 0 && (
              <div className="la__dropdown">
                {candidatosBusca.map((c) => (
                  <button
                    key={String(c.id)}
                    type="button"
                    className="la__item"
                    onClick={() => adicionarAoBuffer(c.id)}
                  >
                    <div className="la__itemName">{c.nome}</div>
                    <div className="la__itemSub">{c.matricula}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {temSelecionados && (
            <div className="la__buffer">
              {selecionadosIds.map((id) => {
                const u = baseUsuarios.find((x) => idsIguais(x.id, id));
                if (!u) return null;
                return (
                  <div className="la__chip" key={String(id)}>
                    <div className="la__chipName">{u.nome}</div>
                    <div className="la__chipSub">{u.user}</div>
                    <button
                      type="button"
                      className="la__chipX"
                      onClick={() => removerDoBuffer(id)}
                      aria-label="Remover da seleção"
                      title="Remover"
                    >
                      <X size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <button
            type="button"
            className={`la__add ${temSelecionados ? "is-active" : "is-muted"}`}
            onClick={handleAdicionarSelecionados}
            disabled={!temSelecionados}
          >
            Adicionar aluno
          </button>

          <div className="la__list">
            {alunos.length > 0 ? (
              alunos.map((a) => (
                <div className="la__row" key={String(a.id)}>
                  <div className="la__info">
                    <div className="la__name">{a.nome}</div>
                    <div className="la__mat">
                      {a.matricula || matriculaPorId.get(String(a.id)) || ""}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="la__x"
                    onClick={() => handleRemoverAluno(a.id)}
                    aria-label={`Remover ${a.nome}`}
                    title="Remover"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))
            ) : (
              <div className="la__empty">Nenhum aluno adicionado.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
